import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { hydratedInput } from "@via/pages/projects/pipelines/workflow/utils/constants.tsx";
import {
  cleanProcessName,
  getInputParamLabels,
} from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import { displayNameOptions } from "@via/utils/form-validators.tsx";
import { classNames } from "@via/utils/helpers";
import { UpdateInputParameterPayload } from "@via/utils/interfaces.tsx";
import { Fragment, type JSX, ReactNode, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Tooltip } from "react-tooltip";

export default function EditInputModal({
  data,
  onSubmit,
  onCancel,
  isEditingAllowed,
}): JSX.Element {
  const [draft, setDraft] = useState(hydratedInput);
  const [isShowSettingsChecked, setIsShowSettingsChecked] = useState(false);
  const {
    register,
    handleSubmit,
    setFocus,
    reset,
    setValue,
    formState: { errors },
  } = useForm<UpdateInputParameterPayload>({
    mode: "onChange",
    defaultValues: draft,
  });

  useEffect(() => {
    if (data) {
      const merged = { ...hydratedInput, ...data?.settings };
      setFocus("label");
      setDraft(merged);
      reset(merged);
      setIsShowSettingsChecked(data?.settings?.showSett !== undefined);
    }
  }, [data]);

  const getTooltipContent = (key): ReactNode => {
    switch (key) {
      case "defVal":
        return <span>Quotes are not allowed.</span>;
      case "dropDown":
        return (
          <span>
            Options need to be entered in comma separated <br />
            format and without quotes (e.g. single,pair).
          </span>
        );
      case "showSett":
        return (
          <span>
            Specify the settings of a connected process <br />
            on the Inputs Section of the Runs page. <br />
            Quotes are not allowed.
          </span>
        );
      case "inDescOpt":
        return (
          <span>HTML syntax or formatted text are acceptable formats.</span>
        );
      default:
        return;
    }
  };

  const shouldShowTooltip = key => {
    return !(key === "name" || key === "inLabelOpt");
  };

  const handleSubmitInputChanges = form => {
    const payload = { ...form };
    if (!isShowSettingsChecked) {
      delete payload?.showSett;
    }
    onSubmit(payload);
  };

  const handleLabelOnBlur = e => {
    let value = e.target.value;
    value = cleanProcessName(value);
    setValue("label", value);
  };

  return (
    <form
      className="flex h-full w-[600px] flex-col divide-gray-200 bg-white"
      onSubmit={e => {
        e.preventDefault();
        void handleSubmit(handleSubmitInputChanges)(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="modal-header">
          <h3 className="mb-4 text-lg font-medium leading-6 text-gray-900">
            Edit Input
          </h3>
        </div>
      </div>
      <div className="modal-body pb-4">
        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200">
            <div>
              <div className="my-2 flex flex-row items-center">
                <label className="flex w-80 flex-row items-center text-sm font-medium leading-6 text-gray-900">
                  Display Name
                  <span className="pl-1 text-red-400">*</span>
                </label>
                <input
                  {...register("label", displayNameOptions.label)}
                  type="text"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  defaultValue={data?.label}
                  onBlur={handleLabelOnBlur}
                  disabled={!isEditingAllowed}
                />
              </div>
              {errors?.label && (
                <span className="text-xs text-rose-500">
                  {errors?.label?.message}
                </span>
              )}
              {Object.entries(draft).map(([key, value]: any[], i) => (
                <Fragment key={i}>
                  <div className="my-6 flex flex-row items-center">
                    <label className="flex w-80 flex-row items-center justify-between text-sm font-medium leading-6 text-gray-900">
                      {getInputParamLabels(key)}
                      {key === "showSett" && (
                        <input
                          type="checkbox"
                          checked={isShowSettingsChecked}
                          onChange={() =>
                            setIsShowSettingsChecked(!isShowSettingsChecked)
                          }
                          className="ml-2"
                          disabled={!isEditingAllowed}
                        />
                      )}
                      {shouldShowTooltip(key) && (
                        <InformationCircleIcon id={key} className="mx-4 h-4" />
                      )}
                    </label>
                    <input
                      {...register(key)}
                      type="text"
                      disabled={
                        (key === "showSett" && !isShowSettingsChecked) ||
                        !isEditingAllowed
                      }
                      className={classNames(
                        "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6",
                        key === "showSett" && !isShowSettingsChecked
                          ? "bg-gray-100"
                          : ""
                      )}
                      defaultValue={value}
                    />
                  </div>
                  <Tooltip
                    anchorSelect={`#${key}`}
                    offset={6}
                    place="right"
                    render={() => <>{getTooltipContent(key)}</>}
                  />
                </Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-footer flex justify-end space-x-2">
        <button
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={() => onCancel()}>
          Cancel
        </button>
        <button
          type="submit"
          disabled={!isEditingAllowed}
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          Save
        </button>
      </div>
    </form>
  );
}
